import { actionNames as calendarActionNames } from '../../../../shared/configs/calendar';
import { detailsPageModeMap } from '../../../../shared/configs/searchParams';
import { dateTime, formatDate } from '../../../../shared/packages/dateTime';
import { AcceptanceDesk } from './acceptanceDesk';
import { blockStatuses } from './constants';
import { ModeSegmentFactory } from './modeSegment';
import { SelectedPeriodInfo } from './selectedPeriodInfo';

const dayWeights = {
  [calendarActionNames.uploadDayRecords]: 0,
  [calendarActionNames.deleteDayIntervals]: 1,
  [calendarActionNames.addDayIntervals]: 2,
  [calendarActionNames.unblockDay]: 3,
  [calendarActionNames.blockDay]: 4,
};

const deskWeights = {
  [calendarActionNames.deleteDeskIntervals]: 0,
  [calendarActionNames.addDeskIntervals]: 1,
  [calendarActionNames.unblockDesk]: 2,
  [calendarActionNames.blockDesk]: 3,
};

const intervalActionNamesByState = {
  'block=false&busy=false&hasResult=false&role=director': [
    calendarActionNames.blockInterval,
    calendarActionNames.deleteInterval,
  ],
  'block=true&busy=false&hasResult=false&role=director': [
    calendarActionNames.unblockInterval,
    calendarActionNames.deleteInterval,
  ],
  'block=false&busy=true&hasResult=false&role=director': [
    calendarActionNames.blockInterval,
    calendarActionNames.reRegistration,
    calendarActionNames.clearInterval,
    calendarActionNames.updateRegistration,
  ],
  'block=false&busy=true&hasResult=false&role=manager': [
    calendarActionNames.reRegistration,
    calendarActionNames.clearInterval,
    calendarActionNames.updateRegistration,
  ],
  'block=true&busy=true&hasResult=false&role=director': [
    calendarActionNames.unblockInterval,
  ],
};
export class CalendarManager {
  constructor(fromDate, toDate, desks) {
    this._period = { fromDate: dateTime(fromDate), toDate: dateTime(toDate) };
    this._modeSegments = new ModeSegmentFactory(this._period);
    this._acceptanceDesks = desks.map((desk) => new AcceptanceDesk(desk));
    this._cachedActiveDesksPayload = new Map();
  }

  getDesks() {
    return this._acceptanceDesks;
  }

  setSelectedPeriodInfo(data) {
    this._selectedPeriodInfo = new SelectedPeriodInfo(data);
    return this;
  }

  getPageCount(mode) {
    return this._modeSegments.getSegment(mode)?.pageCount ?? 0;
  }

  getPeriodAsParams(mode, page) {
    return this._modeSegments.getSegment(mode)?.getPeriodAsParams(page);
  }

  getDates(mode, page) {
    return this._modeSegments.getSegment(mode)?.getDates(page) ?? [];
  }

  getDeskDayIntervals(deskId, isoDateStr) {
    return (
      this._selectedPeriodInfo?.getDeskDayIntervals(deskId, isoDateStr) ?? []
    );
  }

  getIntervalActionsData(interval, date) {
    return {
      targetDate: formatDate(date),
      interval: interval,
      blockedIntervalIds: interval.blocked ? [interval.id] : [],
      unblockedIntervalIds: interval.blocked ? [] : [interval.id],
      getActionNames: ({ pageMode, role }) =>
        this._createIntervalActionNames({ interval, pageMode, role }),
    };
  }

  getDayActionsData(date, dateStr) {
    if (!this._dayHasAction(date) || !this._selectedPeriodInfo) return null;

    const summary = this._selectedPeriodInfo.getDaySummary(dateStr);

    const result = {
      getActionNames: ({ role }) =>
        this._createDayActionNames({ summary, role }),
      dateId: this._selectedPeriodInfo.getDateId(dateStr),
      targetDate: dateStr,
      ...this._getActiveDesksPayload(date, dateStr),
    };

    if (summary) {
      result.existingIntervalIds = summary.allIntervals;
      result.blockedIntervalIds = summary.blockedIntervals;
      result.unblockedIntervalIds = summary.unblockedIntervals;
    }

    return result;
  }

  getDeskActionsData(deskId, date, dateStr) {
    if (!this._selectedPeriodInfo) return null;

    const desk = this._acceptanceDesks.find((desk) => desk.id === deskId);

    if (!desk || !desk._hasActions(date, dateStr)) return null;

    const summary = this._selectedPeriodInfo.getDeskDaySummary(deskId, dateStr);

    const actionNames = this._createDeskActionNames(summary);

    const result = {
      actionNames,
      dateId: this._selectedPeriodInfo.getDateId(dateStr),
      targetDate: dateStr,
      deskId: desk.id,
      deskName: desk.name,
    };

    if (summary) {
      result.existingIntervalIds = summary.allIntervals;
      result.blockedIntervalIds = summary.blockedIntervals;
      result.unblockedIntervalIds = summary.unblockedIntervals;
    }

    return result;
  }

  getInitialPage(mode, date = dateTime()) {
    return this._modeSegments.getSegment(mode)?.getPageForDate(date) ?? 1;
  }

  _dayHasAction(date) {
    const { fromDate, toDate } = this._period;
    return date.isBetween(fromDate, toDate, 'day', '[]');
  }

  _createIntervalActionNames({ interval, pageMode, role }) {
    const { isBusy, blocked, hasResult } = interval;
    const isBusyOrBlocked = isBusy || blocked;

    if (pageMode === detailsPageModeMap.registration) {
      return isBusyOrBlocked ? undefined : [calendarActionNames.registration];
    }

    if (pageMode === detailsPageModeMap.reRegistration) {
      return isBusyOrBlocked
        ? undefined
        : [calendarActionNames.selectIntervalForReRegistration];
    }

    return intervalActionNamesByState[
      `block=${blocked}&busy=${isBusy}&hasResult=${hasResult ?? false}&role=${
        role.status
      }`
    ];
  }

  _createDeskActionNames(summary) {
    const result = [calendarActionNames.addDeskIntervals];

    if (!summary) return result;

    if (summary.deletable) {
      result.push(calendarActionNames.deleteDeskIntervals);
    }

    if (summary.blockStatus === blockStatuses.blocked) {
      result.push(calendarActionNames.unblockDesk);
    }

    if (summary.blockStatus === blockStatuses.unblocked) {
      result.push(calendarActionNames.blockDesk);
    }

    if (summary.blockStatus === blockStatuses.indeterminate) {
      result.push(
        calendarActionNames.blockDesk,
        calendarActionNames.unblockDesk
      );
    }

    return result.sort((a, b) => deskWeights[b] - deskWeights[a]);
  }

  _createDayActionNames({ summary, role }) {
    if (role.isReader) {
      return [calendarActionNames.uploadDayRecords];
    }

    const result = [
      calendarActionNames.addDayIntervals,
      calendarActionNames.uploadDayRecords,
    ];

    if (!summary) return result;

    if (summary.deletable) {
      result.push(calendarActionNames.deleteDayIntervals);
    }

    if (summary.blockStatus === blockStatuses.blocked) {
      result.push(calendarActionNames.unblockDay);
    }

    if (summary.blockStatus === blockStatuses.unblocked) {
      result.push(calendarActionNames.blockDay);
    }

    if (summary.blockStatus === blockStatuses.indeterminate) {
      result.push(calendarActionNames.blockDay, calendarActionNames.unblockDay);
    }

    return result.sort((a, b) => dayWeights[b] - dayWeights[a]);
  }

  _getActiveDesksPayload(date, isoDateStr) {
    const cached = this._cachedActiveDesksPayload.get(isoDateStr);

    if (cached !== undefined) return cached;

    const result = { deskIds: [], deskNames: [] };

    this._acceptanceDesks.forEach((desk) => {
      if (desk._hasActions(date, isoDateStr)) {
        result.deskIds.push(desk.id);
        result.deskNames.push(desk.name);
      }
    });

    this._cachedActiveDesksPayload.set(isoDateStr, result);

    return result;
  }
}
